/**
 * Dashboard page — /campaigns/device-targeting.
 *
 * Hosts <DeviceTargetingForm /> inside the Trackerity dash chrome.
 *
 * Routing note:
 *   The D01 scaffold uses programmatic TanStack Router wiring in router.tsx.
 *   This file exports both:
 *     1. `CampaignsDeviceTargetingPage` — the React component for pairing with
 *         `createRoute({ path: '/campaigns/device-targeting', component })`.
 *     2. `campaignsDeviceTargetingRoute` — a pre-built `createRoute` object the
 *         scaffold/root agent can addChildren into the route tree directly.
 *   Either entry point works; the root wire-up agent picks one.
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { DeviceTargetingForm } from "@/features/device-targeting/DeviceTargetingForm";

/**
 * The page itself. Pure presentation + a single feature form mount.
 */
export function CampaignsDeviceTargetingPage() {
	return (
		<main className="mx-auto flex min-h-screen w-full max-w-4xl flex-col gap-8 px-6 py-16">
			<nav className="flex items-center gap-2 text-xs font-medium text-zinc-500">
				<a
					href="/"
					className="rounded-[6px] px-2 py-1 transition hover:bg-zinc-100 hover:text-zinc-700"
				>
					Dashboard
				</a>
				<span aria-hidden="true">/</span>
				<a
					href="/campaigns"
					className="rounded-[6px] px-2 py-1 transition hover:bg-zinc-100 hover:text-zinc-700"
				>
					Campaigns
				</a>
				<span aria-hidden="true">/</span>
				<span className="rounded-[6px] px-2 py-1 text-zinc-800">
					Device targeting
				</span>
			</nav>

			<DeviceTargetingForm />

			<footer className="border-t border-zinc-200 pt-6 text-xs text-zinc-500">
				Rules evaluated at the edge. Changes take effect within seconds across
				all regional endpoints.
			</footer>
		</main>
	);
}

/**
 * TanStack Router registration object. Root/scaffold agent merges this into
 * the route tree with `rootRoute.addChildren([..., campaignsDeviceTargetingRoute])`.
 */
export const campaignsDeviceTargetingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/campaigns/device-targeting",
	component: CampaignsDeviceTargetingPage,
});

export default CampaignsDeviceTargetingPage;
